/**
 * WHAT AN OBJECT IS, EXACTLY (spec §61D).
 *
 * A placed object carries the numbers somebody will eventually buy against:
 * a vanity is 48" wide, a sink base is 30", a wall cabinet is 36" high and
 * hangs 54" off the floor. Those numbers are purchasing data, not drawing
 * decoration — they drive the plan, the conflict engine, the estimate and
 * the product search.
 *
 * `config` is ONE structured slot on the placed object, interpreted by its
 * own definition: a vanity's sink arrangement, a corner cabinet's shape, a
 * laundry pair's configuration. It is never a second object model, and an
 * object whose definition has nothing to configure simply leaves it null.
 *
 * Pure domain: no React, no SQL, no geometry engine.
 */
import { PU_PER_INCH } from "../geometry/units";

const inches = (n: number): number => Math.round(n * PU_PER_INCH);

/** A size somebody can actually buy. */
export interface SizePreset {
  /** Stable id stored nowhere — presets are a UI convenience over real numbers. */
  id: string;
  label: string;
  widthPu: number;
  depthPu: number;
  heightInches: number;
  /** Structured configuration this preset implies, when it implies one. */
  config?: string;
}

/* ------------------------------------------------------------------ *
 * Vanities (spec §61E)
 * ------------------------------------------------------------------ */

/**
 * Manufactured bathroom vanity widths, audited against what is actually
 * stocked: 24 / 30 / 36 / 48 / 60 / 72 inches, with 60" and 72" the usual
 * double-sink sizes. A pedestal sink is not a vanity at all, but it is what
 * people choose INSTEAD of one, so it belongs in the same picker.
 *
 * Depth 21" is the standard vanity cabinet depth; height 34.5" is the
 * standard finished vanity height (32" for the pedestal's fixture).
 */
export const VANITY_PRESETS: readonly SizePreset[] = [
  { id: "pedestal", label: "Pedestal sink", widthPu: inches(24), depthPu: inches(20), heightInches: 32, config: "pedestal" },
  { id: "single-24", label: '24" single', widthPu: inches(24), depthPu: inches(21), heightInches: 34, config: "single" },
  { id: "single-30", label: '30" single', widthPu: inches(30), depthPu: inches(21), heightInches: 34, config: "single" },
  { id: "single-36", label: '36" single', widthPu: inches(36), depthPu: inches(21), heightInches: 34, config: "single" },
  { id: "single-48", label: '48" single', widthPu: inches(48), depthPu: inches(21), heightInches: 34, config: "single" },
  { id: "double-60", label: '60" double', widthPu: inches(60), depthPu: inches(21), heightInches: 34, config: "double" },
  { id: "double-72", label: '72" double', widthPu: inches(72), depthPu: inches(21), heightInches: 34, config: "double" },
];

export type VanityConfig = "pedestal" | "single" | "double";

export function vanityConfigLabel(config: string | null): string {
  switch (config) {
    case "pedestal":
      return "Pedestal sink";
    case "double":
      return "Double sink";
    case "single":
      return "Single sink";
    default:
      return "";
  }
}

/* ------------------------------------------------------------------ *
 * Cabinet modules (spec §61G)
 * ------------------------------------------------------------------ */

/**
 * Cabinet classes. The remembered-height preference (spec §61I) is kept per
 * CLASS, so a 96" pantry never turns the next base cabinet into a tower.
 */
export type CabinetClass = "base" | "wall" | "tall" | "bath" | "vanity";

const CABINET_CLASS_BY_DEFINITION: Readonly<Record<string, CabinetClass>> = {
  "obj-base-cabinet": "base",
  "obj-drawer-base": "base",
  "obj-sink-base": "base",
  "obj-corner-base": "base",
  "obj-base-cabinets": "base",
  "obj-island-cabinets": "base",
  "obj-wall-cabinet": "wall",
  "obj-corner-wall": "wall",
  "obj-upper-cabinets": "wall",
  "obj-tall-cabinet": "tall",
  "obj-bath-cabinet": "bath",
  "obj-vanity": "vanity",
};

export function cabinetClassOf(definitionId: string): CabinetClass | null {
  return CABINET_CLASS_BY_DEFINITION[definitionId] ?? null;
}

/**
 * Standard manufactured cabinet widths, in inches. Stock cabinetry comes in
 * 3" increments from 9" to 48"; these are the ones actually on the shelf.
 */
export const CABINET_WIDTHS_IN: readonly number[] = [9, 12, 15, 18, 21, 24, 27, 30, 33, 36, 42, 48];

/** Standard heights per class, in inches. */
export const CABINET_HEIGHTS_IN: Readonly<Record<CabinetClass, readonly number[]>> = {
  base: [34, 36],
  wall: [12, 15, 18, 24, 30, 36, 42],
  tall: [84, 90, 96],
  bath: [48, 60, 72, 84],
  vanity: [32, 34, 36],
};

/** Standard depths per class, in inches. */
export const CABINET_DEPTHS_IN: Readonly<Record<CabinetClass, number>> = {
  base: 24,
  wall: 12,
  tall: 24,
  bath: 21,
  vanity: 21,
};

/** How high off the floor this class hangs, in inches. */
export const CABINET_MOUNT_HEIGHT_IN: Readonly<Record<CabinetClass, number>> = {
  base: 0,
  wall: 54,
  tall: 0,
  bath: 0,
  vanity: 0,
};

/** Corner cabinet shapes — chosen from the picture, stored as a value. */
export type CornerCabinetShape = "diagonal" | "blind-left" | "blind-right" | "lazy-susan";

export const CORNER_SHAPES: ReadonlyArray<{ id: CornerCabinetShape; label: string; note: string }> = [
  { id: "diagonal", label: "Diagonal corner", note: "Angled front across the corner." },
  { id: "blind-left", label: "Blind corner (left)", note: "Runs into the corner from the left; part of the box is unreachable." },
  { id: "blind-right", label: "Blind corner (right)", note: "Runs into the corner from the right." },
  { id: "lazy-susan", label: "Lazy Susan", note: "Square corner box with a rotating shelf." },
];

export function cornerShapeOf(config: string | null): CornerCabinetShape {
  const shapes = CORNER_SHAPES.map((s) => s.id) as string[];
  return (config && shapes.includes(config) ? config : "diagonal") as CornerCabinetShape;
}

/** Cabinet module presets for a class: real widths at the class's depth/height. */
export function cabinetPresets(cabinetClass: CabinetClass, heightInches: number): SizePreset[] {
  const depth = CABINET_DEPTHS_IN[cabinetClass];
  return CABINET_WIDTHS_IN.map((w) => ({
    id: `${cabinetClass}-${w}`,
    label: `${w}"`,
    widthPu: inches(w),
    depthPu: inches(depth),
    heightInches,
  }));
}

/* ------------------------------------------------------------------ *
 * Laundry (spec §61F)
 * ------------------------------------------------------------------ */

export type LaundryConfig = "side-by-side" | "stacked" | "all-in-one";

export function laundryConfigOf(definitionId: string): LaundryConfig | null {
  switch (definitionId) {
    case "obj-washer":
    case "obj-dryer":
      return "side-by-side";
    case "obj-laundry-stacked":
      return "stacked";
    case "obj-laundry-combo":
      return "all-in-one";
    default:
      return null;
  }
}

/* ------------------------------------------------------------------ *
 * Elevation — what makes a conflict a fact (spec §61C)
 * ------------------------------------------------------------------ */

/**
 * The vertical band an object occupies, in inches from the finished floor.
 * `null` means NOBODY HAS SAID — the conflict engine must not invent it.
 */
export interface ElevationBand {
  bottomInches: number | null;
  topInches: number | null;
}

export function elevationBand(input: {
  mountHeightInches: number | null;
  heightInches: number | null;
  definitionMountHeightInches?: number | null;
  definitionHeightInches?: number | null;
}): ElevationBand {
  const bottom = input.mountHeightInches ?? input.definitionMountHeightInches ?? null;
  const height = input.heightInches ?? input.definitionHeightInches ?? null;
  if (bottom === null || height === null) return { bottomInches: bottom, topInches: null };
  return { bottomInches: bottom, topInches: bottom + height };
}

/**
 * Do two vertical bands actually share space? `null` = not knowable, which
 * is a different answer from "no" and must stay different all the way to
 * the user (spec §61B).
 */
export function bandsOverlap(a: ElevationBand, b: ElevationBand): boolean | null {
  if (a.bottomInches === null || b.bottomInches === null) return null;
  const aTop = a.topInches;
  const bTop = b.topInches;
  if (aTop === null || bTop === null) return null;
  return a.bottomInches < bTop && b.bottomInches < aTop;
}
